import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress
} from '@mui/material';
import QRCode from 'qrcode';
import { authFetch } from '../utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';

interface Material {
    id: string;
    name: string;
    itemNumber: number;
    currentStock: number;
    unit: string;
    color?: string;
    type: string;
    isMaster?: boolean;
    parentMaterialId?: string;
}

interface CountSession {
    sessionId: string;
    createdAt: string;
    expiresAt: string;
}

const InventoryCountSheet: React.FC = () => {
    // OS-INV-04: tenant-aware API + route base. Defaults to Made.bb when no
    // TenantProvider wraps this component (existing Made.bb route).
    const { apiBase, routeBase } = useTenant();
    const [materials, setMaterials] = useState<Material[]>([]);
    const [session, setSession] = useState<CountSession | null>(null);
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initializeCountSheet = async () => {
            try {
                console.log('🔄 Creating count session...');
                // Create a new count session
                const sessionResponse = await authFetch(`${apiBase}/count-sessions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ createdBy: 'inventory-manager' })
                });

                console.log('📊 Session response status:', sessionResponse.status);

                if (!sessionResponse.ok) {
                    const errorText = await sessionResponse.text();
                    console.error('❌ Session creation failed:', errorText);
                    throw new Error(`Failed to create count session: ${sessionResponse.status} ${errorText}`);
                }

                const sessionData = await sessionResponse.json();
                console.log('✅ Session created:', sessionData);
                setSession(sessionData.session);

                // Generate QR code URL
                const countUrl = `${window.location.origin}${routeBase}/count/${sessionData.session.sessionId}`;
                console.log('🔗 QR URL:', countUrl);
                const qrDataUrl = await QRCode.toDataURL(countUrl, {
                    width: 200,
                    margin: 1
                });
                setQrCodeUrl(qrDataUrl);
                console.log('✅ QR code generated');

                // Fetch materials
                console.log('📦 Fetching materials...');
                const materialsResponse = await authFetch(`${apiBase}/materials`);
                console.log('📊 Materials response status:', materialsResponse.status);

                if (!materialsResponse.ok) {
                    const errorText = await materialsResponse.text();
                    console.error('❌ Materials fetch failed:', errorText);
                    throw new Error(`Failed to fetch materials: ${materialsResponse.status}`);
                }

                const materialsData = await materialsResponse.json();
                console.log('📦 Materials data:', materialsData);

                // Sort materials by item number and filter out master materials
                const materialsList = materialsData.materials || [];
                console.log('📋 Total materials:', materialsList.length);

                // Count materials by category for diagnostics
                const masterCount = materialsList.filter((m: Material) => m.isMaster).length;
                const withItemNumberCount = materialsList.filter((m: Material) => m.itemNumber).length;
                const withoutItemNumberCount = materialsList.filter((m: Material) => !m.itemNumber).length;

                console.log('📊 Materials breakdown:');
                console.log(`   - Master materials: ${masterCount}`);
                console.log(`   - With item numbers: ${withItemNumberCount}`);
                console.log(`   - Without item numbers: ${withoutItemNumberCount}`);

                const sortedMaterials = materialsList
                    .filter((m: Material) => !m.isMaster && m.itemNumber)
                    .sort((a: Material, b: Material) => a.itemNumber - b.itemNumber);

                console.log('✅ Filtered and sorted materials:', sortedMaterials.length);

                if (sortedMaterials.length === 0 && materialsList.length > 0) {
                    console.warn('⚠️ No materials with item numbers found!');
                    console.warn('   This may indicate the item numbers migration has not been run.');
                    console.warn('   Run: npm run migrate:item-numbers on Railway');
                }

                setMaterials(sortedMaterials);
            } catch (error) {
                console.error('💥 Error initializing count sheet:', error);
                alert(`Error: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };

        initializeCountSheet();
    }, [apiBase, routeBase]);

    useEffect(() => {
        // Auto-print when loaded
        if (!loading && materials.length > 0 && qrCodeUrl) {
            setTimeout(() => {
                window.print();
            }, 500);
        }
    }, [loading, materials, qrCodeUrl]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, '@media print': { p: 2 } }}>
            {/* Header with QR Code */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Inventory Count Sheet
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Session ID: {session?.sessionId.substring(0, 8)}...
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Created: {session && new Date(session.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Expires: {session && new Date(session.expiresAt).toLocaleString()}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    {qrCodeUrl && (
                        <>
                            <img src={qrCodeUrl} alt="Count Session QR Code" style={{ width: '150px', height: '150px' }} />
                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                Scan to count
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* Instructions */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', '@media print': { border: '1px solid #ccc' } }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Instructions:
                </Typography>
                <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
                    <li>Scan the QR code with your mobile device to access the count entry page</li>
                    <li>Count physical inventory for each item</li>
                    <li>Enter the 6-digit item number on your device</li>
                    <li>Enter the actual count when prompted</li>
                    <li>System will automatically update inventory levels</li>
                </Typography>
            </Paper>

            {/* Warning if no materials found */}
            {materials.length === 0 && (
                <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff3cd', border: '2px solid #ffc107' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#856404' }}>
                        ⚠️ No Materials Found
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, color: '#856404' }}>
                        No materials with item numbers were found. This usually means the item numbers migration has not been run yet.
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#fff', p: 1, borderRadius: 1, color: '#000' }}>
                        To fix this, run the following on Railway:<br/>
                        <strong>npm run migrate:item-numbers && npm start</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: '#856404' }}>
                        After the migration completes, change the start command back to: <strong>npm start</strong>
                    </Typography>
                </Paper>
            )}

            {/* Materials Table */}
            <Table sx={{ '@media print': { fontSize: '10pt' } }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#1976d2' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '120px' }}>
                            Item #
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                            Material Name
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '100px' }}>
                            Type
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '120px', textAlign: 'right' }}>
                            System Count
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '80px', textAlign: 'center' }}>
                            Unit
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '150px', borderLeft: '2px solid white' }}>
                            Physical Count
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {materials.map((material, index) => (
                        <TableRow
                            key={material.id}
                            sx={{
                                backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9',
                                '@media print': {
                                    pageBreakInside: 'avoid',
                                    borderBottom: '1px solid #ddd'
                                }
                            }}
                        >
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '14pt' }}>
                                {material.itemNumber.toString().padStart(6, '0')}
                            </TableCell>
                            <TableCell>{material.name}</TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{material.type}</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                                {material.currentStock}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{material.unit}</TableCell>
                            <TableCell sx={{ borderLeft: '2px solid #ddd' }}>
                                {/* Empty cell for manual counting */}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', '@media print': { mt: 2 } }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    Made.bb Inventory Management System • {materials.length} items listed
                </Typography>
            </Box>
        </Box>
    );
};

export default InventoryCountSheet;
